"use client";

import { motion } from "framer-motion";
import { Coins, Flame, TrendingUp, Users } from "lucide-react";

interface EconomicLoopProps {
	language: "zh" | "en";
}

export function EconomicLoop({ language }: EconomicLoopProps) {
	return (
		<div className="w-full py-12 flex justify-center">
			{/* 
        Ensuring 1:1 Aspect Ratio for the orbital container. 
        This guarantees the center is truly equidistance from all poles.
      */}
			<div className="relative w-full max-w-md aspect-square flex items-center justify-center overflow-visible">
				{/* Decorative Radar Rings */}
				<div className="absolute inset-0 border border-primary/5 rounded-full animate-pulse-slow" />
				<div className="absolute inset-[-20%] border border-primary/5 rounded-full animate-pulse-slow animation-delay-1000" />

				{/* Central Token */}
				<div className="absolute z-20 flex flex-col items-center justify-center">
					<motion.div
						initial={{ scale: 0.9, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						className="w-28 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-slate-800 to-slate-950 dark:from-slate-900 dark:to-black flex items-center justify-center shadow-[0_0_40px_rgba(0,0,0,0.3)] dark:shadow-[0_0_40px_rgba(76,175,80,0.2)] z-20 border border-white/10"
					>
						<div className="text-center text-white">
							<div className="text-xl md:text-2xl font-black tracking-tighter mb-0.5">
								21M
							</div>
							<div className="text-[9px] md:text-[10px] uppercase tracking-widest opacity-60 font-bold leading-none">
								{language === "zh" ? "总量恒定" : "Fixed Supply"}
							</div>
						</div>
					</motion.div>

					{/* Outer Rotating Data Ring */}
					<motion.div
						animate={{ rotate: 360 }}
						transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
						className="absolute inset-[-10px] md:inset-[-15px] rounded-full border border-dashed border-primary/20"
					/>
				</div>

				{/* Orbiting Elements - Exact positioning on a perfect circle */}
				<div className="absolute inset-0">
					{/* Top: Usage */}
					<motion.div
						className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center z-30"
						animate={{ y: [0, -5, 0] }}
						transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
					>
						<div className="w-12 h-12 md:w-16 md:h-16 bg-blue-500 rounded-2xl flex items-center justify-center shadow-lg mb-2 border border-white/20 relative group">
							<div className="absolute inset-0 bg-blue-400 blur-lg opacity-0 group-hover:opacity-40 transition-opacity" />
							<Users className="w-6 h-6 md:w-8 md:h-8 text-white relative z-10" />
						</div>
						<div className="flex flex-col items-center">
							<span className="text-[9px] md:text-[10px] font-black uppercase tracking-wider bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-3 py-1 rounded-full border border-slate-200 dark:border-white/10 shadow-xl whitespace-nowrap">
								{language === "zh" ? "真实使用" : "Real Usage"}
							</span>
							<div className="w-px h-4 bg-gradient-to-b from-blue-500/50 to-transparent" />
						</div>
					</motion.div>

					{/* Right: Revenue */}
					<motion.div
						className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 flex items-center z-30"
						animate={{ x: [0, 5, 0] }}
						transition={{
							duration: 4,
							delay: 1,
							repeat: Infinity,
							ease: "easeInOut",
						}}
					>
						<div className="h-px w-4 md:w-8 bg-gradient-to-l from-green-500/50 to-transparent mr-2" />
						<div className="flex flex-col items-center">
							<div className="w-12 h-12 md:w-16 md:h-16 bg-green-500 rounded-2xl flex items-center justify-center shadow-lg mb-2 border border-white/20 relative group">
								<div className="absolute inset-0 bg-green-400 blur-lg opacity-0 group-hover:opacity-40 transition-opacity" />
								<Coins className="w-6 h-6 md:w-8 md:h-8 text-white relative z-10" />
							</div>
							<span className="text-[9px] md:text-[10px] font-black uppercase tracking-wider bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-3 py-1 rounded-full border border-slate-200 dark:border-white/10 shadow-xl whitespace-nowrap">
								{language === "zh" ? "产生收益" : "Revenue"}
							</span>
						</div>
					</motion.div>

					{/* Bottom: Burn */}
					<motion.div
						className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 flex flex-col items-center z-30"
						animate={{ y: [0, 5, 0] }}
						transition={{
							duration: 4,
							delay: 2,
							repeat: Infinity,
							ease: "easeInOut",
						}}
					>
						<div className="w-px h-4 md:h-8 bg-gradient-to-t from-red-500/50 to-transparent" />
						<div className="w-12 h-12 md:w-16 md:h-16 bg-red-500 rounded-2xl flex items-center justify-center shadow-lg mb-2 border border-white/20 relative group">
							<div className="absolute inset-0 bg-red-400 blur-lg opacity-0 group-hover:opacity-40 transition-opacity" />
							<Flame className="w-6 h-6 md:w-8 md:h-8 text-white relative z-10" />
						</div>
						<span className="text-[9px] md:text-[10px] font-black uppercase tracking-wider bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-3 py-1 rounded-full border border-slate-200 dark:border-white/10 shadow-xl whitespace-nowrap">
							{language === "zh" ? "持续销毁" : "Continuous Burn"}
						</span>
					</motion.div>

					{/* Left: Value */}
					<motion.div
						className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 flex items-center z-30"
						animate={{ x: [0, -5, 0] }}
						transition={{
							duration: 4,
							delay: 3,
							repeat: Infinity,
							ease: "easeInOut",
						}}
					>
						<div className="flex flex-col items-center">
							<div className="w-12 h-12 md:w-16 md:h-16 bg-purple-500 rounded-2xl flex items-center justify-center shadow-lg mb-2 border border-white/20 relative group">
								<div className="absolute inset-0 bg-purple-400 blur-lg opacity-0 group-hover:opacity-40 transition-opacity" />
								<TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-white relative z-10" />
							</div>
							<span className="text-[9px] md:text-[10px] font-black uppercase tracking-wider bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-3 py-1 rounded-full border border-slate-200 dark:border-white/10 shadow-xl whitespace-nowrap">
								{language === "zh" ? "价值沉淀" : "Value Accrual"}
							</span>
						</div>
						<div className="h-px w-4 md:w-8 bg-gradient-to-r from-purple-500/50 to-transparent ml-2" />
					</motion.div>
				</div>

				{/* Orbit Path (SVG) */}
				<svg
					className="absolute inset-0 w-full h-full pointer-events-none opacity-30 dark:opacity-40"
					style={{ zIndex: 0 }}
				>
					<circle
						cx="50%"
						cy="50%"
						r="50%"
						fill="none"
						stroke="currentColor"
						strokeWidth="0.5"
						strokeDasharray="4 4"
						className="text-primary/30"
					/>
					<defs>
						<radialGradient id="tokenGlow" cx="50%" cy="50%" r="50%">
							<stop offset="0%" stopColor="rgba(76, 175, 80, 0.15)" />
							<stop offset="100%" stopColor="transparent" />
						</radialGradient>
					</defs>
					<circle cx="50%" cy="50%" r="45%" fill="url(#tokenGlow)" />
				</svg>
			</div>
		</div>
	);
}
